import { AlertTriangle } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  instagramDestination,
  useChannelDestinations,
  useSaveInstagramDestination,
} from '@/api/endpoints/destinations'
import {
  useChannelEngagement,
  useEngagementPosts,
  usePatchEngagement,
} from '@/api/endpoints/engagement'
import { useFactoryPresets } from '@/api/endpoints/factoryPresets'
import {
  useCortesSources,
  usePatchChannelSchedule,
  useSaveCortesSources,
  useSchedulableFactories,
  useSchedulerConfig,
  type ChannelConfigOut,
  type ChannelScheduleStateOut,
} from '@/api/endpoints/scheduler'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Spinner } from '@/components/ui/Spinner'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { Toggle } from '@/components/ui/Toggle'
import { Tooltip } from '@/components/ui/Tooltip'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { PlatformChip } from '@/features/platforms/PlatformChip'
import { cn } from '@/lib/cn'
import { disabledLast, mergeWithKnownFormats } from '@/lib/videoFormats'
import {
  DEFAULT_UPLOAD_TZ,
  DESTINATION_FIELDS,
  destinationErrors,
  divergentFields,
  hasDestinationChanges,
  inactiveDestinationFields,
  rebaseDestination,
  sameDestinationValues,
  toDestinationPatch,
  toDestinationValues,
  type DestinationValues,
} from './destinations'
import { DestinationsTab, type DestinationServerChange } from './DestinationsTab'
import { GenerationTab } from './GenerationTab'
import { SourceTab } from './SourceTab'
import { StyleTab } from './StyleTab'
import { InteractionsTab } from './InteractionsTab'
import {
  changedAreas,
  CORTES_FACTORY,
  diffValues,
  ENGAGEMENT_FIELDS,
  engagementSchema,
  inactiveEngagementFields,
  NO_PRESET_VALUE,
  SCHEDULE_FIELDS,
  sameSources,
  scheduleSchema,
  sourceRowFromApi,
  summaryParts,
  TAB_LABELS,
  tabOfField,
  toEngagementPatch,
  toEngagementValues,
  toSchedulePatch,
  toScheduleValues,
  toSourcePayload,
  usesActiveColor,
  validateSources,
  type Change,
  type EngagementValues,
  type ScheduleValues,
  type SourceRow,
  type TabId,
} from './model'
import { PostsTab } from './PostsTab'
import { UploadTab } from './UploadTab'
import styles from './ChannelEditDialog.module.scss'

export interface ChannelEditDialogProps {
  open: boolean
  onClose: () => void
  slug: string
  /** Config atual do canal (valores iniciais das abas Geração e Upload). */
  config: ChannelConfigOut
  /** Nome de exibição do canal (não o slug). */
  name?: string
  platform?: 'youtube' | 'tiktok'
  /** Estado do scheduler — a próxima geração e o próximo upload do resumo. */
  state?: ChannelScheduleStateOut | undefined
  /** Aba que abre primeiro (o chip "N rascunhos para aprovar" abre em Posts). */
  initialTab?: TabId
  /** Rola até "Fila e recentes" ao abrir na aba Posts. */
  focusQueue?: boolean
  /** Edita a geração de um pool: só as abas Geração e Estilo de vídeo, sem marca nem plataforma. */
  poolMode?: boolean
}

const TAB_ORDER: TabId[] = ['source', 'generation', 'style', 'upload', 'posts', 'interactions', 'destinations']

const DESTINATION_ERROR_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(DESTINATION_FIELDS).map(([k, v]) => [k, v.label]),
)

/** Onde focar quando o Salvar encontra um erro na aba Destinos. */
function destinationFieldId(field: string): string | undefined {
  const pause = /^pause_windows\.(\d+)$/.exec(field)
  if (pause) return `ig-pause-${pause[1] ?? '0'}-from`
  return (
    {
      offset_minutes: 'ig-offset',
      max_per_day: 'ig-max',
      caption_cta: 'ig-cta',
      hashtags_max: 'ig-hashtags',
    } as Record<string, string>
  )[field]
}

const SCHEDULE_ERROR_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(SCHEDULE_FIELDS).map(([k, v]) => [k, v.label]),
)
const ENGAGEMENT_ERROR_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(ENGAGEMENT_FIELDS).map(([k, v]) => [k, v.label]),
)

/** Onde focar quando o Salvar encontra um erro. */
const FIELD_IDS: Record<string, string> = {
  factory: 'ch-factory',
  generation_time: 'ch-gen-time',
  videos_per_day: 'ch-vpd',
  upload_time: 'ch-upl-time',
  uploads_per_day: 'ch-upd',
  publish_window_start: 'ch-window-start',
  publish_window_end: 'ch-window-end',
  backlog_max_age_days: 'ch-backlog-age',
  max_uploads_per_cycle: 'ch-max-uploads',
  posts_per_week: 'ch-posts-week',
  posts_days: 'ch-posts-days',
  posts_time: 'ch-posts-time',
  posts_types: 'ch-posts-types',
  hearts_per_day: 'ch-hearts-day',
  hearts_start: 'ch-hearts-start',
  hearts_end: 'ch-hearts-end',
  pin_delay_hours: 'ch-pin-delay',
}

let sourceKeySeq = 0
const nextSourceKey = () => ++sourceKeySeq

function issuesOf(result: { success: boolean; error?: { issues: ReadonlyArray<{ path: PropertyKey[]; message: string }> } }) {
  const out: Record<string, string> = {}
  if (result.success || !result.error) return out
  for (const issue of result.error.issues) {
    const key = String(issue.path[0] ?? '')
    if (key && !(key in out)) out[key] = issue.message
  }
  return out
}

/**
 * Modal "Editar canal" em abas — Geração · Upload · Posts · Interações (card #255).
 *
 * As duas primeiras gravam na config do scheduler (e nas fontes do Cortes); as
 * duas novas, na config de engajamento. O Salvar grava tudo; Aprovar, Editar e
 * Rejeitar um post valem na hora. Fechar com alteração pendente pede
 * confirmação. Microcopy e regras: o canvas aprovado (docs/plan-studio-engagement.md).
 */
export function ChannelEditDialog({
  open,
  onClose,
  slug,
  config,
  name,
  platform = 'youtube',
  state,
  initialTab = 'generation',
  focusQueue = false,
  poolMode = false,
}: ChannelEditDialogProps) {
  const isYoutube = platform === 'youtube'
  // Pool de onde o canal tira os vídeos (só leitura aqui; a aba Origem liga e desliga).
  const poolSlug = config.pool_slug ?? null
  // Canal ligado a pool não gera: a aba Geração some, e a casa do diálogo
  // passa a ser Upload. A configuração de geração dele fica guardada e volta
  // a valer quando ele sair do pool.
  const hideGeneration = poolSlug !== null && !poolMode
  const homeTab: TabId = hideGeneration ? 'upload' : 'generation'
  // A ordem de formatos do upload de um canal ligado é sobre os vídeos do pool:
  // os formatos são os da factory do pool, não os da agenda do canal.
  const schedulerConfig = useSchedulerConfig()
  const poolFactory = poolSlug ? schedulerConfig.data?.channels[poolSlug]?.factory : undefined
  const formatFactory = hideGeneration && poolFactory ? poolFactory : config.factory
  const patchSchedule = usePatchChannelSchedule(slug)
  const saveSources = useSaveCortesSources(slug)
  const patchEngagement = usePatchEngagement(slug)
  const saveDestination = useSaveInstagramDestination(slug)
  const { data: schedulableFactories = [] } = useSchedulableFactories()

  // ------------------------------------------------------------ estado
  const [initialSched, setInitialSched] = useState<ScheduleValues>(() => toScheduleValues(config, formatFactory))
  const [sched, setSchedState] = useState<ScheduleValues>(initialSched)
  const [initialSlots, setInitialSlots] = useState<string[]>(config.publish_slots)
  const [slots, setSlots] = useState<string[]>(config.publish_slots)
  const [initialSources, setInitialSources] = useState<SourceRow[] | null>(null)
  const [sources, setSources] = useState<SourceRow[]>([])
  const [initialEng, setInitialEng] = useState<EngagementValues | null>(null)
  const [eng, setEngState] = useState<EngagementValues | null>(null)
  const [initialDest, setInitialDest] = useState<DestinationValues | null>(null)
  const [dest, setDestState] = useState<DestinationValues | null>(null)
  // O destino no servidor não é o da tela: mudou com edição não salva na aba
  // (``rebased``), ou o PUT devolveu outro estado (``saved``).
  const [destServerChange, setDestServerChange] = useState<DestinationServerChange | null>(null)
  const [tab, setTab] = useState<TabId>(initialTab)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  // Reabrir zera tudo a partir do que está salvo.
  useEffect(() => {
    if (!open) return
    const fresh = toScheduleValues(config, formatFactory)
    setInitialSched(fresh)
    setSchedState(fresh)
    setInitialSlots(config.publish_slots)
    setSlots(config.publish_slots)
    setInitialSources(null)
    setSources([])
    setInitialEng(null)
    setEngState(null)
    setInitialDest(null)
    setDestState(null)
    setDestServerChange(null)
    const wanted: TabId = isYoutube
      ? initialTab
      : initialTab === 'posts' || initialTab === 'interactions' || initialTab === 'destinations'
        ? 'generation'
        : initialTab
    setTab(wanted === 'generation' ? homeTab : wanted)
    setConfirmOpen(false)
    patchSchedule.reset()
    saveSources.reset()
    patchEngagement.reset()
    saveDestination.reset()
    // Só ao abrir (ou trocar de canal): um refetch da config não pode apagar a edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slug])

  // A aba Origem liga e desliga o pool na hora, fora do Salvar, e o backend
  // troca a geração do canal (desligar copia a do pool). O formulário volta ao
  // servidor quando o pool muda: senão o Salvar seguinte mandaria a geração
  // antiga de volta. A aba aberta fica onde está.
  const lastPool = useRef(config.pool_slug ?? null)
  useEffect(() => {
    const current = config.pool_slug ?? null
    if (!open || lastPool.current === current) return
    lastPool.current = current
    const fresh = toScheduleValues(config, formatFactory)
    setInitialSched(fresh)
    setSchedState(fresh)
    setInitialSlots(config.publish_slots)
    setSlots(config.publish_slots)
    setInitialSources(null)
    setSources([])
  }, [open, config, formatFactory])

  const isCortes = sched.factory === CORTES_FACTORY
  const sourcesQuery = useCortesSources(slug, open && isCortes)
  useEffect(() => {
    if (!open || initialSources !== null || !sourcesQuery.data) return
    const rows = sourcesQuery.data.map((it) => sourceRowFromApi(it, nextSourceKey()))
    setInitialSources(rows)
    setSources(rows)
  }, [open, initialSources, sourcesQuery.data])

  const engQuery = useChannelEngagement(slug, open && isYoutube)
  const postsQuery = useEngagementPosts(slug, open && isYoutube)
  useEffect(() => {
    if (!open || initialEng !== null || !engQuery.data) return
    const values = toEngagementValues(engQuery.data.config)
    setInitialEng(values)
    setEngState(values)
  }, [open, initialEng, engQuery.data])

  // Destinos (Instagram…): ``null`` = backend sem as rotas (o front sai antes do
  // pull do skab) — a aba nem aparece. As pausas são lidas e escritas no fuso
  // do ciclo de upload, então os valores só nascem com ele em mãos.
  const destQuery = useChannelDestinations(slug, open && isYoutube)
  const igDest = instagramDestination(destQuery.data)
  const showDestinations = isYoutube && igDest !== null
  const tabVisible = (t: TabId): boolean => {
    if (poolMode) return t === 'generation' || t === 'style'
    // O estilo é da geração: canal ligado a pool publica o que o pool gera.
    if (t === 'generation' || t === 'style') return !hideGeneration
    if (t === 'source') return isYoutube
    return t !== 'destinations' || showDestinations
  }
  const uploadTz = schedulerConfig.data?.upload.timezone || DEFAULT_UPLOAD_TZ
  const tzReady = schedulerConfig.data !== undefined || schedulerConfig.isError
  useEffect(() => {
    if (!open || initialDest !== null || !igDest || !tzReady) return
    const values = toDestinationValues(igDest, uploadTz)
    setInitialDest(values)
    setDestState(values)
  }, [open, initialDest, igDest, tzReady, uploadTz])
  // O formulário pode nascer do cache, e o servidor muda sozinho (o disjuntor
  // desliga o destino; outra aba salva). Quando a releitura traz outro valor, o
  // formulário passa a ele em tudo o que o dono não editou, e avisa se havia
  // edição. Durante o Salvar, quem atualiza é o próprio save.
  useEffect(() => {
    if (!open || !igDest || !tzReady || initialDest === null || dest === null || saving) return
    const server = toDestinationValues(igDest, uploadTz)
    if (sameDestinationValues(server, initialDest)) return
    const rebased = rebaseDestination(initialDest, dest, server)
    setInitialDest(rebased.initial)
    setDestState(rebased.current)
    if (rebased.hadEdits) {
      setDestServerChange({ kind: 'rebased', fields: rebased.conflicts.map((key) => DESTINATION_FIELDS[key].label) })
    }
  }, [open, igDest, tzReady, uploadTz, initialDest, dest, saving])
  // A aba some se a rota deixar de responder (ex.: um rollback do backend).
  useEffect(() => {
    if (tab === 'destinations' && destQuery.data === null) setTab(homeTab)
  }, [tab, destQuery.data, homeTab])

  const { data: presets = [], isPending: presetsPending, isError: presetsError } = useFactoryPresets(sched.factory)
  // Preset é por factory: o que não pertence à factory atual volta a "Sem preset".
  // Lista que não carregou não prova nada — sem ela, o preset fica como está.
  // Com a aba Geração escondida (canal no pool), nada muda nela sozinho: viraria
  // uma "alteração não salva" que a pessoa não tem como ver.
  useEffect(() => {
    if (hideGeneration || presetsPending || presetsError) return
    if (sched.preset_id !== NO_PRESET_VALUE && !presets.some((p) => p.id === sched.preset_id)) {
      setSchedState((s) => ({ ...s, preset_id: NO_PRESET_VALUE }))
    }
  }, [hideGeneration, sched.factory, sched.preset_id, presets, presetsPending, presetsError])

  const setSched = useCallback(
    <K extends keyof ScheduleValues>(key: K, value: ScheduleValues[K]) => {
      setSchedState((s) => {
        if (key === 'factory' && value !== s.factory) {
          if (value === initialSched.factory) {
            // Voltar à factory salva é o mesmo que desfazer a troca.
            return {
              ...s,
              factory: initialSched.factory,
              preset_id: initialSched.preset_id,
              upload_priority_types: initialSched.upload_priority_types,
              disabled_video_types: initialSched.disabled_video_types,
            }
          }
          // Trocar a factory troca os formatos que existem e o preset deixa de valer.
          return {
            ...s,
            factory: value as string,
            upload_priority_types: mergeWithKnownFormats(value as string, []),
            disabled_video_types: [],
            preset_id: NO_PRESET_VALUE,
          }
        }
        if (key === 'use_peak_hour' && value === false && s.publish_window_end <= s.publish_window_start) {
          // A janela some com o pico desligado; inválida, ela volta ao que estava salvo
          // em vez de deixar um erro escondido bloqueando o Salvar.
          return {
            ...s,
            use_peak_hour: false,
            publish_window_start: initialSched.publish_window_start,
            publish_window_end: initialSched.publish_window_end,
          }
        }
        if (key === 'subtitle_style' && !usesActiveColor(value as string)) {
          // A cor só vale no karaokê: num estilo que não a lê, volta ao salvo se
          // ele também não a lia, para não sobrar uma "alteração" invisível.
          const keep = usesActiveColor(initialSched.subtitle_style) ? '' : initialSched.subtitle_active_color
          return { ...s, subtitle_style: value as string, subtitle_active_color: keep }
        }
        return { ...s, [key]: value }
      })
    },
    [initialSched],
  )

  const setEng = useCallback(<K extends keyof EngagementValues>(key: K, value: EngagementValues[K]) => {
    setEngState((s) => (s ? { ...s, [key]: value } : s))
  }, [])

  const setDest = useCallback(<K extends keyof DestinationValues>(key: K, value: DestinationValues[K]) => {
    setDestState((s) => (s ? { ...s, [key]: value } : s))
  }, [])

  // ------------------------------------------------------------ validação
  const schedErrors = useMemo(() => issuesOf(scheduleSchema.safeParse(sched)), [sched])
  const engErrors = useMemo(() => (eng ? issuesOf(engagementSchema.safeParse(eng)) : {}), [eng])
  const destErrors = useMemo(() => (dest && showDestinations ? destinationErrors(dest) : {}), [dest, showDestinations])
  const sourcesError = useMemo(
    () => (isCortes && initialSources !== null && !sameSources(initialSources, sources) ? validateSources(sources) : null),
    [isCortes, initialSources, sources],
  )

  const errorCount: Record<TabId, number> = {
    source: 0,
    generation: 0,
    style: 0,
    upload: 0,
    posts: 0,
    interactions: 0,
    destinations: 0,
  }
  for (const field of [...Object.keys(schedErrors), ...Object.keys(engErrors)]) {
    const t = tabOfField(field)
    if (t !== 'header') errorCount[t] += 1
  }
  if (sourcesError) errorCount.generation += 1
  errorCount.destinations = Object.keys(destErrors).length
  const totalErrors = TAB_ORDER.reduce((n, t) => n + errorCount[t], 0)

  // ------------------------------------------------------------ alterações
  const changes: Change[] = useMemo(() => {
    const out = diffValues(initialSched, sched, SCHEDULE_FIELDS)
    if (JSON.stringify(initialSlots) !== JSON.stringify(slots)) {
      out.push({
        field: 'publish_slots',
        tab: 'upload',
        label: 'Slots de publicação',
        before: initialSlots.join(' · ') || 'nenhum',
        after: slots.join(' · ') || 'nenhum',
      })
    }
    if (isCortes && initialSources !== null && !sameSources(initialSources, sources)) {
      out.push({
        field: 'sources',
        tab: 'generation',
        label: 'Fontes de cortes',
        before: `${String(initialSources.length)} fonte(s)`,
        after: `${String(sources.length)} fonte(s)`,
      })
    }
    if (initialEng && eng) out.push(...diffValues(initialEng, eng, ENGAGEMENT_FIELDS, inactiveEngagementFields(eng)))
    if (showDestinations && initialDest && dest) {
      // Prefixo: ``enabled`` também existe na agenda (o "Ativo" do cabeçalho).
      out.push(
        ...diffValues(initialDest, dest, DESTINATION_FIELDS, inactiveDestinationFields(dest)).map((c) => ({
          ...c,
          field: `instagram.${c.field}`,
        })),
      )
    }
    return out
  }, [
    initialSched,
    sched,
    initialSlots,
    slots,
    isCortes,
    initialSources,
    sources,
    initialEng,
    eng,
    showDestinations,
    initialDest,
    dest,
  ])

  const dirtyTabs = new Set(changes.map((c) => c.tab))
  const factoryLabels = new Map(schedulableFactories.map((f) => [f.slug, f.label] as const))
  const schedBefore = (key: keyof ScheduleValues) => {
    const change = changes.find((c) => c.field === key)
    if (!change) return undefined
    return key === 'factory' ? (factoryLabels.get(initialSched.factory) ?? initialSched.factory) : change.before
  }
  const engBefore = (key: keyof EngagementValues) => changes.find((c) => c.field === key)?.before
  const destBefore = (key: keyof DestinationValues) => changes.find((c) => c.field === `instagram.${key}`)?.before

  const factoryChoices = schedulableFactories.some((f) => f.slug === sched.factory)
    ? schedulableFactories
    : sched.factory
      ? [{ slug: sched.factory, label: sched.factory }, ...schedulableFactories]
      : schedulableFactories

  // ------------------------------------------------------------ fila de posts
  // O chip "N rascunhos" rola até a fila uma vez por abertura, quando ela monta.
  const scrolledToQueue = useRef(false)
  useEffect(() => {
    if (open) scrolledToQueue.current = false
  }, [open, slug])
  const queueRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el || !focusQueue || scrolledToQueue.current) return
      scrolledToQueue.current = true
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ block: 'start' })
      })
    },
    [focusQueue],
  )

  // ------------------------------------------------------------ ações
  function requestClose() {
    if (saving) return
    if (changes.length) setConfirmOpen(true)
    else onClose()
  }

  function goToFirstError() {
    const fields = [...Object.keys(schedErrors), ...Object.keys(engErrors)]
    const first = TAB_ORDER.find((t) => errorCount[t] > 0 && tabVisible(t)) ?? homeTab
    setTab(first)
    let id: string | undefined
    if (first === 'destinations') {
      const destField = Object.keys(destErrors)[0]
      id = destField ? destinationFieldId(destField) : undefined
    } else {
      const field = fields.find((f) => tabOfField(f) === first)
      id = field ? FIELD_IDS[field] : undefined
    }
    window.setTimeout(() => {
      const el = id ? document.getElementById(id) : null
      el?.scrollIntoView({ block: 'center' })
      // Grupo (dias, tipos): o foco vai para o primeiro controle dentro dele.
      const target = el?.matches('input, button, textarea, select') ? el : el?.querySelector<HTMLElement>('input, button')
      target?.focus()
    }, 50)
  }

  async function save() {
    if (saving) return
    if (totalErrors > 0) {
      // "Salvar e fechar" com erro: sai da confirmação e leva ao campo.
      setConfirmOpen(false)
      goToFirstError()
      return
    }
    setSaving(true)
    try {
      // Cada parte salva vira o novo "salvo": se a seguinte falhar, o rodapé e o
      // "Sair sem salvar?" só falam do que de fato ficou pendente.
      const schedChanged = changes.some(
        (c) =>
          c.field !== 'sources' &&
          (c.tab === 'header' || c.tab === 'generation' || c.tab === 'style' || c.tab === 'upload'),
      )
      if (schedChanged) {
        await patchSchedule.mutateAsync(toSchedulePatch(sched, slots, initialSched))
        // O PATCH manda os desativados para o fim; a tela passa a mostrar o salvo.
        const savedSched = {
          ...sched,
          upload_priority_types: disabledLast(sched.upload_priority_types, sched.disabled_video_types),
        }
        setInitialSched(savedSched)
        setSchedState(savedSched)
        setInitialSlots(slots)
      }
      if (isCortes && initialSources !== null && !sameSources(initialSources, sources)) {
        await saveSources.mutateAsync(toSourcePayload(sources))
        setInitialSources(sources)
      }
      if (eng && changes.some((c) => c.tab === 'posts' || c.tab === 'interactions')) {
        await patchEngagement.mutateAsync(toEngagementPatch(eng))
        setInitialEng(eng)
      }
      const destPatch =
        showDestinations && initialDest && dest && hasDestinationChanges(changes)
          ? toDestinationPatch(initialDest, dest)
          : null
      if (destPatch) {
        // Só o que o dono mudou: um campo que ele não tocou fica como o servidor
        // tem (o disjuntor pode ter desligado o destino com a aba aberta). Só
        // resolve quando a resposta traz o que foi enviado (sem "salvo" falso).
        const saved = instagramDestination(await saveDestination.mutateAsync(destPatch))
        const savedValues = saved ? toDestinationValues(saved, uploadTz) : null
        const fresh = savedValues ?? dest
        // A resposta é a verdade: o que a tela mostrava pode não estar gravado
        // (o disjuntor desligou o destino com a aba aberta, o publicador sumiu).
        const diverged = savedValues && dest ? divergentFields(dest, savedValues) : []
        setInitialDest(fresh)
        setDestState(fresh)
        if (diverged.length) {
          // Não fecha em silêncio: o dono sairia achando que o destino publica.
          setDestServerChange({ kind: 'saved', fields: diverged.map((key) => DESTINATION_FIELDS[key].label) })
          setTab('destinations')
          setConfirmOpen(false)
          return
        }
        setDestServerChange(null)
      }
      setConfirmOpen(false)
      onClose()
    } catch {
      // O erro aparece no topo do corpo (FormErrorSummary); o modal fica aberto.
      setConfirmOpen(false)
    } finally {
      setSaving(false)
    }
  }

  // ------------------------------------------------------------ cabeçalho
  const engData = engQuery.data
  const summary = summaryParts({
    enabled: sched.enabled,
    platform,
    nextGeneration: state?.next_run_at ?? null,
    nextUpload: state?.upload_next_run_at ?? null,
    postsEnabled: Boolean(eng?.posts_enabled),
    postsPerWeek: eng?.posts_per_week ?? 0,
    heartsEnabled: Boolean(eng?.hearts_enabled),
    heartsPerDay: eng?.hearts_per_day ?? 0,
    nextScheduledPost: engData?.next_scheduled_post ?? null,
  })
  const sessionExpired = engData?.session.state === 'expired'
  const displayName = name ?? slug

  const statusText =
    totalErrors > 0
      ? `${String(totalErrors)} ${totalErrors === 1 ? 'erro' : 'erros'} em ${TAB_ORDER.filter((t) => errorCount[t] > 0)
          .map((t) => TAB_LABELS[t])
          .join(', ')} · corrija para salvar`
      : changes.length
        ? `${String(changes.length)} ${changes.length === 1 ? 'alteração não salva' : 'alterações não salvas'} · ${changedAreas(changes).join(', ')}`
        : 'Sem alterações'

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
    >
      <TabsRoot
        value={tab}
        onValueChange={(v) => {
          const next = v as TabId
          if (!tabVisible(next)) return
          if (!isYoutube && (next === 'posts' || next === 'interactions')) return
          setTab(next)
        }}
      >
        <DialogContent
          className={styles.dialog}
          bodyClassName={styles.body}
          title={
            <span className={styles.titleRow}>
              <span className={styles.titleText}>{displayName}</span>
              {poolMode ? <span className={styles.poolChip}>Pool</span> : <PlatformChip platform={platform} />}
            </span>
          }
          description={
            <span className={styles.summary}>
              <span className={styles.summaryLong}>
                {summary.long.map((part, i) => (
                  <span key={part}>
                    {i > 0 ? <span className={styles.summarySep}>· </span> : null}
                    {part}
                  </span>
                ))}
              </span>
              <span className={styles.summaryShort}>{summary.short.join(' · ')}</span>
            </span>
          }
          headerActions={
            <>
              <span className={styles.activeToggle}>
                {sched.enabled ? 'Ativo' : 'Pausado'}
                <Toggle
                  aria-label="Canal ativo"
                  checked={sched.enabled}
                  onCheckedChange={(v) => {
                    setSched('enabled', v)
                  }}
                />
              </span>
              <span className={styles.headerSep} aria-hidden />
            </>
          }
          subheader={
            <div className={styles.subheader}>
              {!sched.enabled && initialSched.enabled ? (
                <Callout
                  role="alert"
                  className={styles.pausedCallout}
                  title="Ao salvar, o canal fica pausado"
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSched('enabled', true)
                      }}
                    >
                      Manter ativo
                    </Button>
                  }
                >
                  Para geração, upload, posts novos e corações. Posts já programados no YouTube ainda saem.
                </Callout>
              ) : null}
              <TabsList aria-label="Seções do canal" className={styles.tabsList} reorderKey="channel-edit">
                {/* Destinos só com backend que tem as rotas (e canal do YouTube). */}
                {TAB_ORDER.filter(tabVisible).map((t) => (
                  // The key is what the saved tab order goes by — keep it the tab id.
                  <TabTrigger
                    key={t}
                    tab={t}
                    disabled={!isYoutube && (t === 'posts' || t === 'interactions')}
                    errors={errorCount[t]}
                    dirty={dirtyTabs.has(t)}
                    attention={sessionExpired && (t === 'posts' || t === 'interactions')}
                  />
                ))}
              </TabsList>
            </div>
          }
          footer={
            <div className={styles.footer}>
              <span className={cn(styles.footerStatus, totalErrors > 0 && styles.footerStatusError)} aria-live="polite">
                {statusText}
              </span>
              <Button variant="secondary" disabled={saving} onClick={requestClose}>
                Cancelar
              </Button>
              <Button
                leadingIcon={saving ? <Spinner /> : undefined}
                aria-disabled={saving}
                onClick={() => {
                  void save()
                }}
              >
                {saving ? 'Salvando…' : 'Salvar'}
              </Button>
            </div>
          }
        >
          {patchSchedule.isError ? (
            <FormErrorSummary error={patchSchedule.error} fieldLabels={SCHEDULE_ERROR_LABELS} />
          ) : null}
          {patchEngagement.isError ? (
            <FormErrorSummary error={patchEngagement.error} fieldLabels={ENGAGEMENT_ERROR_LABELS} />
          ) : null}
          {saveSources.isError ? (
            <FormErrorSummary error={saveSources.error} title="As fontes do Cortes não foram salvas" />
          ) : null}
          {saveDestination.isError ? (
            <FormErrorSummary
              error={saveDestination.error}
              title="O destino do Instagram não foi salvo"
              fieldLabels={DESTINATION_ERROR_LABELS}
            />
          ) : null}

          {!poolMode && isYoutube ? (
            <TabsContent value="source" forceMount className={styles.tabContent}>
              <SourceTab slug={slug} name={displayName} poolSlug={poolSlug} isYoutube={isYoutube} />
            </TabsContent>
          ) : null}
          <TabsContent value="generation" forceMount className={styles.tabContent}>
            <GenerationTab
              values={sched}
              errors={schedErrors}
              set={setSched}
              before={schedBefore}
              factoryChoices={factoryChoices}
              factoryChanged={sched.factory !== initialSched.factory}
              onUndoFactory={() => {
                setSchedState((s) => ({
                  ...s,
                  factory: initialSched.factory,
                  preset_id: initialSched.preset_id,
                  upload_priority_types: initialSched.upload_priority_types,
                  disabled_video_types: initialSched.disabled_video_types,
                }))
              }}
              presets={presets}
              presetsPending={presetsPending}
              sources={sources}
              sourcesLoading={sourcesQuery.isPending && initialSources === null}
              sourcesReady={initialSources !== null}
              sourcesLoadError={sourcesQuery.isError}
              isYoutube={isYoutube}
              sourcesError={sourcesError}
              saveSourcesError={saveSources.isError ? saveSources.error : null}
              onSourcesChange={setSources}
              newSourceKey={nextSourceKey}
            />
          </TabsContent>
          <TabsContent value="style" forceMount className={styles.tabContent}>
            <StyleTab
              values={sched}
              set={setSched}
              before={schedBefore}
              factoryLabel={factoryLabels.get(sched.factory) ?? sched.factory}
            />
          </TabsContent>
          <TabsContent value="upload" forceMount className={styles.tabContent}>
            <UploadTab
              values={sched}
              errors={schedErrors}
              set={setSched}
              before={schedBefore}
              slots={slots}
              slotsBefore={changes.find((c) => c.field === 'publish_slots')?.before}
              onSlotsChange={setSlots}
              formatFactory={formatFactory}
            />
          </TabsContent>
          {isYoutube ? (
            <>
              <TabsContent value="posts" forceMount className={styles.tabContent}>
                {eng ? (
                  <PostsTab
                    slug={slug}
                    values={eng}
                    errors={engErrors}
                    set={setEng}
                    before={engBefore}
                    automation={engData?.automation}
                    session={engData?.session}
                    posts={postsQuery.data}
                    postsLoading={postsQuery.isPending}
                    queueRef={queueRef}
                  />
                ) : (
                  <EngagementLoading error={engQuery.isError} />
                )}
              </TabsContent>
              <TabsContent value="interactions" forceMount className={styles.tabContent}>
                {eng ? (
                  <InteractionsTab
                    channelName={displayName}
                    channelSlug={slug}
                    values={eng}
                    errors={engErrors}
                    set={setEng}
                    before={engBefore}
                    automation={engData?.automation}
                    session={engData?.session}
                    pin={engData?.pin}
                    pinEligibility={engData?.pin_eligibility}
                    savedPinEnabled={engData?.config.pin_enabled ?? false}
                    recent={engData?.recent}
                  />
                ) : (
                  <EngagementLoading error={engQuery.isError} />
                )}
              </TabsContent>
            </>
          ) : null}
          {showDestinations && destQuery.data ? (
            <TabsContent value="destinations" forceMount className={styles.tabContent}>
              {dest && initialDest ? (
                <DestinationsTab
                  slug={slug}
                  destinations={destQuery.data}
                  instagram={igDest}
                  values={dest}
                  savedEnabled={initialDest.enabled}
                  errors={destErrors}
                  set={setDest}
                  before={destBefore}
                  timezone={uploadTz}
                  serverChange={destServerChange}
                  onDismissServerChange={() => {
                    setDestServerChange(null)
                  }}
                />
              ) : (
                <div className={styles.section}>
                  <span className={styles.empty}>Carregando…</span>
                </div>
              )}
            </TabsContent>
          ) : null}
        </DialogContent>
      </TabsRoot>

      <ConfirmClose
        open={confirmOpen}
        changes={changes}
        saving={saving}
        onContinue={() => {
          setConfirmOpen(false)
        }}
        onDiscard={() => {
          setConfirmOpen(false)
          onClose()
        }}
        onSave={() => {
          void save()
        }}
      />
    </DialogRoot>
  )
}

function TabTrigger({
  tab,
  disabled,
  errors,
  dirty,
  attention,
}: {
  tab: TabId
  disabled: boolean
  errors: number
  dirty: boolean
  attention: boolean
}) {
  const label = TAB_LABELS[tab]
  const sr = errors
    ? `, ${String(errors)} ${errors === 1 ? 'erro' : 'erros'}`
    : attention
      ? ', sessão do YouTube Studio expirada'
      : dirty
        ? ', alterações não salvas'
        : ''
  const trigger = (
    <TabsTrigger
      value={tab}
      className={cn(styles.tabTrigger, disabled && styles.tabDisabled)}
      aria-disabled={disabled || undefined}
    >
      {label}
      {errors ? (
        <span className={styles.badge} aria-hidden>
          {errors}
        </span>
      ) : attention ? (
        <span className={cn(styles.dot, styles.dotWarning)} aria-hidden />
      ) : dirty ? (
        <span className={styles.dot} aria-hidden />
      ) : null}
      {disabled ? <span className={styles.onlyYoutube}>Só YouTube</span> : null}
      {sr ? <span className={styles.srOnly}>{sr}</span> : null}
    </TabsTrigger>
  )
  if (!disabled) return trigger
  return <Tooltip content="Posts de comunidade e interações só existem para canais do YouTube.">{trigger}</Tooltip>
}

function EngagementLoading({ error }: { error: boolean }) {
  return (
    <div className={styles.section}>
      <span className={styles.empty}>
        {error ? 'Não foi possível carregar a configuração de engajamento deste canal.' : 'Carregando…'}
      </span>
    </div>
  )
}

function ConfirmClose({
  open,
  changes,
  saving,
  onContinue,
  onDiscard,
  onSave,
}: {
  open: boolean
  changes: Change[]
  saving: boolean
  onContinue: () => void
  onDiscard: () => void
  onSave: () => void
}) {
  const areas = changedAreas(changes)
  const where = areas.length > 1 ? `${areas.slice(0, -1).join(', ')} e ${areas.at(-1) ?? ''}` : (areas[0] ?? '')
  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onContinue()
      }}
    >
      <DialogContent
        role="alertdialog"
        className={styles.confirm}
        hideClose
        title={
          <span className={styles.titleRow}>
            <AlertTriangle aria-hidden color="var(--warning)" size={18} />
            Sair sem salvar?
          </span>
        }
        description={`Você tem ${String(changes.length)} ${changes.length === 1 ? 'alteração' : 'alterações'} em ${where}. Se fechar agora, ${changes.length === 1 ? 'ela se perde' : 'elas se perdem'}.`}
        footer={
          <>
            <Button variant="secondary" onClick={onContinue}>
              Continuar editando
            </Button>
            <Button variant="danger" onClick={onDiscard}>
              Descartar
            </Button>
            <Button leadingIcon={saving ? <Spinner /> : undefined} aria-disabled={saving} onClick={onSave}>
              {saving ? 'Salvando…' : 'Salvar e fechar'}
            </Button>
          </>
        }
      >
        <ul className={styles.changeList}>
          {changes.slice(0, 8).map((c) => (
            <li key={c.field} className={styles.changeItem}>
              <span>{c.label}</span>
              <span>
                {c.before} → {c.after}
              </span>
            </li>
          ))}
          {changes.length > 8 ? (
            <li className={styles.changeItem}>
              <span>e mais {changes.length - 8}</span>
              <span />
            </li>
          ) : null}
        </ul>
      </DialogContent>
    </DialogRoot>
  )
}
